import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { cancelAdmission, getCancelReasons } from "../../api/ipdEnhancementsApi";

const OTHER = "__other__";

// Cancel an admission that has not yet had a bed allocated. Offers the
// preconfigured reasons plus a free-text "Other". On success the parent
// refetches its patient list via onCancelled().
export default function CancelModal({ open, onClose, admission, onCancelled }) {
  const [reasons, setReasons] = useState([]);
  const [selected, setSelected] = useState("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected("");
    setOtherText("");
    getCancelReasons()
      .then(setReasons)
      .catch((err) => {
        console.error("Failed to load cancel reasons:", err);
        setReasons([]);
      });
  }, [open]);

  const handleConfirm = async () => {
    if (!selected) {
      toast.error("Please select a reason.");
      return;
    }
    const payload =
      selected === OTHER
        ? { reason: otherText.trim() }
        : { reasonId: selected };
    if (selected === OTHER && !payload.reason) {
      toast.error("Please enter a reason.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await cancelAdmission(admission?.admissionId, payload);
      if (data?.status === "Success") {
        toast.success(data.message || "Admission cancelled.");
        onCancelled?.(data);
        onClose?.();
      } else if (data?.status === "PendingApproval") {
        // G13: role requires supervisor approval — nothing was freed or
        // changed yet, the request just needs to clear the Approvals Center.
        toast.info(
          data.message ||
            "Cancellation requires supervisor approval; request submitted."
        );
        onCancelled?.(data);
        onClose?.();
      } else {
        toast.error(data?.message || "Could not cancel the admission.");
      }
    } catch (err) {
      console.error("Cancel admission failed:", err);
      toast.error(
        err?.response?.data?.message || "Could not cancel the admission."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cancel Admission</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", mb: 1.5 }}>
          Cancelling <strong>{admission?.patientName || "this patient"}</strong>
          's admission will free the held bed. This cannot be undone.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <Select
            value={selected}
            displayEmpty
            onChange={(e) => setSelected(e.target.value)}
            renderValue={(value) => {
              if (!value) return "Select a reason";
              if (value === OTHER) return "Other";
              const row = reasons.find((r) => String(r.id) === String(value));
              return row?.reason || value;
            }}
          >
            <MenuItem value="" disabled>
              Select a reason
            </MenuItem>
            {reasons.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.reason}
              </MenuItem>
            ))}
            <MenuItem value={OTHER}>Other</MenuItem>
          </Select>
        </FormControl>
        {selected === OTHER && (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Reason"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Keep Admission
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "Cancelling..." : "Cancel Admission"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
